/**
 * Environment Configuration Validator
 * 
 * Validates environment configuration at startup to ensure
 * all required values are present and valid
 */

import { Environment, EnvironmentVariables } from './environment.interface';
import { MOCK_FLAG_DEFINITIONS } from '@core/config/mock-flag-definitions';

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export class EnvironmentValidator {
  
  /**
   * Validates the environment configuration
   */
  static validate(env: Environment): ValidationResult {
    const result: ValidationResult = {
      valid: true,
      errors: [],
      warnings: []
    };

    // Validate URLs
    this.validateUrls(env, result);
    
    // Validate feature flags consistency
    this.validateFeatureFlags(env, result);
    
    // Validate security settings
    this.validateSecurity(env, result);
    
    // Validate trading configuration
    this.validateTrading(env, result);
    
    // Validate WebSocket configuration
    this.validateWebSocket(env, result);
    
    // Validate monitoring configuration
    this.validateMonitoring(env, result);

    // Validate cache configuration
    this.validateCache(env, result);

    // Validate bulk operations configuration
    this.validateBulkOperations(env, result);

    // Environment-specific validations
    if (env.production) {
      this.validateProduction(env, result);
    } else if (env.staging) {
      this.validateStaging(env, result);
    } else if (env.development) {
      this.validateDevelopment(env, result);
    }

    result.valid = result.errors.length === 0;
    return result;
  }

  private static validateUrls(env: Environment, result: ValidationResult): void {
    // API URL validation
    if (!env.apiUrl) {
      result.errors.push('API URL is required');
    } else if (!this.isValidUrl(env.apiUrl)) {
      result.errors.push('API URL is not valid');
    }

    // WebSocket URL validation
    if (!env.wsUrl) {
      result.errors.push('WebSocket URL is required');
    } else if (!this.isValidWsUrl(env.wsUrl)) {
      result.errors.push('WebSocket URL is not valid');
    }

    // Protocol consistency
    if (env.production) {
      if (!env.apiUrl.startsWith('https://')) {
        result.errors.push('Production API URL must use HTTPS');
      }
      if (!env.wsUrl.startsWith('wss://')) {
        result.errors.push('Production WebSocket URL must use WSS');
      }
    }
  }

  private static validateFeatureFlags(env: Environment, result: ValidationResult): void {
    // Auto trading safety checks
    if (env.features.enableAutoTrading && env.development) {
      result.warnings.push('Auto trading is enabled in development environment');
    }

    if (env.features.enableAutoTrading && env.features.enableMockData) {
      result.warnings.push('Auto trading with mock data may cause issues');
    }

    // Debug features in production only (devTools/debugPanel are allowed in staging)
    if (env.production) {
      if (env.features.enableDevTools) {
        result.errors.push('Development tools must be disabled in production');
      }
      if (env.features.enableDebugPanel) {
        result.errors.push('Debug panel must be disabled in production');
      }
    }

    // GAP-FE-033: Validate ALL mock flags in production AND staging.
    // Uses the centralized MOCK_FLAG_DEFINITIONS to prevent drift.
    if (env.production || env.staging) {
      const envLabel = env.production ? 'production' : 'staging';
      for (const def of MOCK_FLAG_DEFINITIONS) {
        if (env.features[def.key]) {
          result.errors.push(
            `Mock flag '${def.key}' (${def.label}) must be disabled in ${envLabel}`
          );
        }
      }
    }
  }

  private static validateSecurity(env: Environment, result: ValidationResult): void {
    // MFA requirements
    if (env.production && !env.auth.requireMFA) {
      result.errors.push('Multi-factor authentication is required in production');
    }

    // Password strength
    if (env.auth.passwordMinLength < 8) {
      result.warnings.push('Password minimum length should be at least 8 characters');
    }

    // Session timeout
    if (env.production && env.auth.sessionTimeout > 3600000) { // 1 hour
      result.warnings.push('Session timeout is longer than recommended for production');
    }

    // HTTPS requirements
    if (env.production && !env.security.enableHSTS) {
      result.warnings.push('HSTS should be enabled in production');
    }
  }

  private static validateTrading(env: Environment, result: ValidationResult): void {
    // Order size validation
    if (env.trading.maxOrderSize <= env.trading.minOrderSize) {
      result.errors.push('Maximum order size must be greater than minimum order size');
    }

    // Slippage validation
    if (env.trading.defaultSlippage < 0 || env.trading.defaultSlippage > 10) {
      result.warnings.push('Default slippage seems unusual (should be 0-10%)');
    }

    // Timeout validation
    if (env.trading.orderTimeoutMs < 1000) {
      result.warnings.push('Order timeout is very short, may cause issues');
    }

    // HFT-specific validations
    if (env.production && env.trading.priceUpdateIntervalMs > 1000) {
      result.warnings.push('Price update interval is slow for HFT applications');
    }

    // Risk check frequency
    if (env.trading.riskLimitCheckInterval > env.trading.priceUpdateIntervalMs) {
      result.warnings.push('Risk limit checks should be more frequent than price updates');
    }
  }

  private static validateWebSocket(env: Environment, result: ValidationResult): void {
    // Reconnection settings
    if (env.wsMaxReconnectAttempts < 5) {
      result.warnings.push('WebSocket max reconnect attempts is low for trading applications');
    }

    if (env.wsReconnectInterval > 10000) {
      result.warnings.push('WebSocket reconnect interval is long for real-time trading');
    }

    // Heartbeat validation
    if (env.wsHeartbeatInterval > 60000) {
      result.warnings.push('WebSocket heartbeat interval is long, connections may time out');
    }
  }

  private static validateMonitoring(env: Environment, result: ValidationResult): void {
    // Production monitoring
    if (env.production) {
      if (!env.monitoring.enableErrorReporting) {
        result.warnings.push('Error reporting should be enabled in production');
      }
      if (!env.monitoring.enablePerformanceTracking) {
        result.warnings.push('Performance tracking should be enabled in production');
      }
    }

    // Sample rate validation
    if (env.monitoring.sampleRate < 0 || env.monitoring.sampleRate > 1) {
      result.errors.push('Monitoring sample rate must be between 0 and 1');
    }

    // Performance thresholds for HFT
    if (env.production) {
      if (env.monitoring.performanceThresholds.wsLatency > 100) {
        result.warnings.push('WebSocket latency threshold is high for HFT applications');
      }
      if (env.monitoring.performanceThresholds.apiResponseTime > 1000) {
        result.warnings.push('API response time threshold is high for trading applications');
      }
    }

    // GAP-FE-011: Catch contradictory Sentry config at startup
    if (env.sentry?.enabled && !env.sentry?.dsn) {
      result.warnings.push(
        'Sentry enabled but DSN is empty — error telemetry will be silently dropped'
      );
    }
  }

  private static validateCache(env: Environment, result: ValidationResult): void {
    if (env.cache.defaultTTL <= 0) {
      result.errors.push('Cache defaultTTL must be a positive number');
    }

    if (env.cache.userCacheTTL <= 0) {
      result.errors.push('Cache userCacheTTL must be a positive number');
    }

    if (env.cache.listCacheTTL <= 0) {
      result.errors.push('Cache listCacheTTL must be a positive number');
    }

    if (env.cache.maxCacheSize <= 0) {
      result.errors.push('Cache maxCacheSize must be a positive number');
    }
  }

  private static validateBulkOperations(env: Environment, result: ValidationResult): void {
    if (env.bulkOperations.batchSize <= 0) {
      result.errors.push('Bulk operations batchSize must be a positive number');
    }

    if (env.bulkOperations.batchDelayMs < 0) {
      result.errors.push('Bulk operations batchDelayMs must be non-negative');
    }

    if (env.bulkOperations.maxConcurrentOperations <= 0) {
      result.errors.push('Bulk operations maxConcurrentOperations must be a positive number');
    }

    if (env.bulkOperations.timeoutMs <= 0) {
      result.errors.push('Bulk operations timeoutMs must be a positive number');
    }
  }

  private static validateProduction(env: Environment, result: ValidationResult): void {
    // Exchange configuration — include all configured exchanges
    const exchanges = [env.exchanges.binance, env.exchanges.coinbase, env.exchanges.kraken];
    if (env.exchanges.bitfinex) {
      exchanges.push(env.exchanges.bitfinex);
    }
    const enabledExchanges = exchanges.filter(ex => ex.enabled);

    if (enabledExchanges.length === 0) {
      result.errors.push('At least one exchange must be enabled in production');
    }

    // Sandbox check
    const sandboxExchanges = enabledExchanges.filter(ex => ex.sandbox);
    if (sandboxExchanges.length > 0) {
      result.warnings.push('Sandbox exchanges are enabled in production');
    }

    // Logging
    if (env.logging.enableConsoleLog) {
      result.warnings.push('Console logging should be disabled in production');
    }

    // Performance
    if (env.ui.enableAnimations) {
      result.warnings.push('UI animations should be disabled in production for performance');
    }
  }

  private static validateStaging(env: Environment, result: ValidationResult): void {
    // Staging should use test endpoints where possible
    if (!env.exchanges.binance.sandbox || !env.exchanges.coinbase.sandbox) {
      result.warnings.push('Consider using sandbox exchanges in staging');
    }

    // Auto trading check
    if (env.features.enableAutoTrading) {
      result.warnings.push('Auto trading in staging should be carefully monitored');
    }
  }

  private static validateDevelopment(env: Environment, result: ValidationResult): void {
    // Development safety checks
    if (!env.features.enableMockData) {
      result.warnings.push('Consider enabling mock data in development');
    }

    if (!env.trading.enablePaperTrading) {
      result.warnings.push('Paper trading should be enabled in development');
    }

    // Performance warnings
    if (env.logging.level !== 'debug') {
      result.warnings.push('Debug logging recommended in development');
    }
  }

  private static isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return url.startsWith('http://') || url.startsWith('https://');
    } catch {
      return false;
    }
  }

  private static isValidWsUrl(url: string): boolean {
    try {
      new URL(url);
      return url.startsWith('ws://') || url.startsWith('wss://');
    } catch {
      return false;
    }
  }

  /**
   * Validates environment variables and returns processed values
   */
  static validateEnvironmentVariables(envVars: EnvironmentVariables): {
    valid: boolean;
    errors: string[];
    warnings: string[];
    processed: Partial<Environment>;
  } {
    const result = {
      valid: true,
      errors: [] as string[],
      warnings: [] as string[],
      processed: {} as Partial<Environment>
    };

    // Process and validate each environment variable
    if (envVars.RAZOR_API_URL) {
      if (this.isValidUrl(envVars.RAZOR_API_URL)) {
        result.processed.apiUrl = envVars.RAZOR_API_URL;
      } else {
        result.errors.push('RAZOR_API_URL is not a valid URL');
      }
    }

    if (envVars.RAZOR_WS_URL) {
      if (this.isValidWsUrl(envVars.RAZOR_WS_URL)) {
        result.processed.wsUrl = envVars.RAZOR_WS_URL;
      } else {
        result.errors.push('RAZOR_WS_URL is not a valid WebSocket URL');
      }
    }

    // Validate numeric environment variables
    if (envVars.DATABASE_MAX_CONNECTIONS) {
      const maxConnections = parseInt(envVars.DATABASE_MAX_CONNECTIONS, 10);
      if (isNaN(maxConnections) || maxConnections < 1) {
        result.errors.push('DATABASE_MAX_CONNECTIONS must be a positive number');
      } else {
        result.processed.database = { 
          ...(result.processed.database || {}),
          maxConnections 
        } as any;
      }
    }

    // Validate boolean environment variables
    if (envVars.ENABLE_AUTO_TRADING) {
      const enableAutoTrading = envVars.ENABLE_AUTO_TRADING.toLowerCase() === 'true';
      result.processed.features = {
        ...(result.processed.features || {}),
        enableAutoTrading
      } as any;
      
      if (enableAutoTrading && envVars.DEPLOYMENT_ENVIRONMENT === 'development') {
        result.warnings.push('Auto trading is enabled in development via environment variable');
      }
    }

    // Reject any attempt to enable mock data via runtime environment variables (SEC-001).
    // Mock flags are controlled at build time via environment files only.
    // GAP-FE-033: Uses explicit envVarName from MOCK_FLAG_DEFINITIONS (no regex derivation).
    const rawEnvVars = envVars as Record<string, unknown>;
    for (const mockKey of MOCK_FLAG_DEFINITIONS.map(def => def.envVarName)) {
      if (rawEnvVars[mockKey] !== undefined) {
        result.errors.push(
          `${mockKey} is not a supported runtime override. ` +
          'Mock flags are controlled at build time via environment files only.'
        );
      }
    }

    result.valid = result.errors.length === 0;
    return result;
  }
}

/**
 * Convenience function to validate current environment
 */
export function validateCurrentEnvironment(env: Environment): void {
  // In production-like environments, suppress console output to avoid
  // leaking validation details (expected config, security posture) to DevTools
  if (env.production || env.staging) {
    // Still validate — the result is returned by EnvironmentValidator.validate()
    // and handled programmatically by the caller (EnvironmentLoader)
    return;
  }

  const result = EnvironmentValidator.validate(env);

  if (!result.valid) {
    // eslint-disable-next-line no-console
    console.error('Environment validation failed:');
    // eslint-disable-next-line no-console
    result.errors.forEach(error => console.error(`  ${error}`));
  }

  if (result.warnings.length > 0) {
    // eslint-disable-next-line no-console
    console.warn('Environment validation warnings:');
    // eslint-disable-next-line no-console
    result.warnings.forEach(warning => console.warn(`  ${warning}`));
  }
}
